import { Button, Spacing } from '@vkontakte/vkui';
import React, { type FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiPhotoGenerationPaidLimit, aiPhotoGenerationTariffsModal } from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoGeneration';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { useNextTime } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.clamp.hooks';
import { useTariffBuy } from 'reactApp/modules/payment/hooks/useTariffBuy';
import { openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import LottieFetch from 'reactApp/ui/Lottie/LottieFetch';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './AiPhotoGenerationClamp.css';

interface Props {
    onClose: () => void;
}

const animationSrc = `${cdnPackagesUrl}/static/cloud/ai/photo-generation/clamp/clamp.json`;

export const AiPhotoGenerationClamp: FC<Props> = memo(function AiGenerationClamp({ onClose }) {
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const isCorpOrPaid = isPaidUser || isCorpUser;
    const clampType = isCorpOrPaid ? 'paymium' : 'freemium';

    const dispatch = useDispatch();

    const { data: nextTime } = useNextTime();

    useEffect(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'clamp-show',
            generation_clamp_type: clampType,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'generation_clamp_type']],
        });
    }, []);

    const onClickBuy = useTariffBuy({
        paySource: aiPhotoGenerationTariffsModal.paySource,
    });

    const handleOnClick = useCallback(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'clamp-click',
            generation_clamp_type: clampType,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'generation_clamp_type']],
        });

        if (isCorpOrPaid) {
            onClose();
            return;
        }

        dispatch(
            openBuyDisableAds({
                title: aiPhotoGenerationTariffsModal.buyTitle,
                tariffsNumber: aiPhotoGenerationTariffsModal.tariffsNumber,
                activeTab: aiPhotoGenerationTariffsModal.activeTab,
                isFrame: false,
                onBuyClick: onClickBuy,
                actionShow: aiPhotoGenerationTariffsModal.actionShow,
                actionClick: aiPhotoGenerationTariffsModal.actionClick,
            })
        );
    }, [clampType, isCorpOrPaid, dispatch, onClickBuy, onClose]);

    const title = useMemo(() => {
        if (isCorpOrPaid) {
            return `Вы\u00A0сгенерировали ${aiPhotoGenerationPaidLimit}\u00A0изображений из\u00A0${aiPhotoGenerationPaidLimit}`;
        }

        return `${aiPhotoGenerationPaidLimit}\u00A0генераций в\u00A0день с\u00A0подпиской Mail\u00A0Space`;
    }, [isCorpOrPaid]);

    const button = useMemo(() => {
        if (isCorpOrPaid) {
            return 'Понятно';
        }

        return 'Подключить за\u00A01\u00A0₽';
    }, [isCorpOrPaid]);

    if (!nextTime?.time) {
        return null;
    }

    const timeText = `${nextTime.isTomorrow ? 'завтра\u00A0— ' : ''}в ${nextTime.time}`;
    const subTitle = isCorpOrPaid
        ? `Создать новое можно будет ${timeText}`
        : `Вы\u00A0сгенерировали изображение с\u00A0нейросетью бесплатно. Создать новое можно будет ${timeText}. А\u00A0с\u00A0подпиской\u00A0— сейчас`;

    return (
        <div className={styles.wrapper}>
            <div className={styles.animationContainer}>
                <LottieFetch src={animationSrc} loop={true} autoplay={true} className={styles.animation} />
            </div>
            <Spacing size={32} />
            <div className={styles.title}>{title}</div>
            <Spacing size={24} />
            <div className={styles.subTitle}>{subTitle}</div>
            <Spacing size={42} />
            <Button mode="primary" appearance="accent-invariable" size="l" className={styles.button} onClick={handleOnClick}>
                {button}
            </Button>
        </div>
    );
});

AiPhotoGenerationClamp.displayName = 'AiPhotoGenerationClamp';
